"use client"

import { useTr } from "../../../lib/use-tr"
import { AUDIT_T, KIND_LABELS } from "../../company/audit/translations"
import {
  AlertCircle,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  RotateCw,
} from "lucide-react"
import { useState } from "react"

import { useLeadAuditPdf } from "@/lib/apps/autoleads/hooks/use-audit-templates"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"

import {
  AUDIT_KINDS,
  type AuditKind,
} from "@/nango/types/apps.autoleads/autoleads/AuditTemplate"

// A file name the reader recognises: the lead, then the version.
function pdfFileName(leadName: string, kind: AuditKind): string {
  const slug = leadName
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
  return `audit-${slug || "lead"}-${kind}.pdf`
}

// The signed storage URL serves the PDF inline for the preview. Its `download`
// parameter makes the storage answer with an attachment instead, so the file
// downloads without a backend change; a storage that ignores it just opens it.
function downloadUrl(url: string, fileName: string): string {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set("download", fileName)
    return parsed.toString()
  } catch {
    return url
  }
}

// The lead's branded audit PDF, as its prospect would receive it, shown in
// place: rendered on the spot when stale, in either version.
export function LeadAuditPdfButton({
  leadId,
  leadName,
}: {
  leadId: number
  leadName: string
}) {
  const tr = useTr(AUDIT_T)
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<AuditKind>("minimal")
  const pdf = useLeadAuditPdf(leadId, kind, open)
  const fileName = pdfFileName(leadName, kind)
  const title = tr("leadAuditPdfTitle").replace("{name}", leadName)

  return (
    <>
      {/* A real secondary button, small enough that the accordion row keeps
          its height. It opens the in-app preview (generated on demand). */}
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={() => setOpen(true)}
      >
        <FileText />
        {tr("leadAuditPdf")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col sm:h-[90vh] sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {tr("leadAuditPdfDescription")}
            </DialogDescription>
          </DialogHeader>
          {/* Version on the left, the two ways out on the right: open the
              shareable page, or download the file itself. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              aria-label={tr("versionLabel")}
              value={kind}
              onValueChange={(next) => {
                // Radix sends "" when the pressed item is clicked again.
                if (next) setKind(next as AuditKind)
              }}
            >
              {AUDIT_KINDS.map((version) => (
                <ToggleGroupItem key={version} value={version}>
                  {tr(KIND_LABELS[version])}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {/* On a phone the preview's fallback card carries the actions. */}
            <div className="hidden sm:block">
              <PdfActions
                url={pdf.data?.url}
                link={pdf.data?.link}
                fileName={fileName}
              />
            </div>
          </div>
          <div
            aria-busy={pdf.isFetching}
            className="flex min-h-0 flex-1 flex-col"
          >
            {pdf.data ? (
              <>
                {/* Phones rarely render an embedded PDF, so they get the two
                    actions instead of a blank frame. */}
                <iframe
                  key={pdf.data.url}
                  title={title}
                  src={pdf.data.url}
                  className="border-border hidden min-h-0 flex-1 rounded-lg border sm:block"
                />
                <div className="border-border text-muted-foreground flex flex-col items-center gap-3 rounded-lg border px-4 py-6 text-center text-xs sm:hidden">
                  <FileText className="size-6" aria-hidden />
                  <p>{tr("leadAuditPdfMobileHint")}</p>
                  <PdfActions
                    url={pdf.data.url}
                    link={pdf.data.link}
                    fileName={fileName}
                  />
                </div>
              </>
            ) : pdf.isError ? (
              <div className="border-border flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border px-4 py-8 text-center text-xs">
                <AlertCircle className="text-destructive size-5" aria-hidden />
                <p className="text-foreground font-medium">
                  {tr("leadAuditPdfFailed")}
                </p>
                <p className="text-muted-foreground max-w-sm">
                  {tr("leadAuditPdfFailedHint")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => pdf.refetch()}
                >
                  <RotateCw />
                  {tr("leadAuditPdfRetry")}
                </Button>
              </div>
            ) : (
              // Same frame as the preview, so nothing jumps when it lands.
              <div className="border-border flex flex-1 flex-col gap-4 rounded-lg border p-6">
                <p
                  role="status"
                  className="text-muted-foreground flex items-center gap-2 text-xs"
                >
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  {tr("leadAuditPdfLoading")}
                </p>
                <div className="flex flex-col gap-2.5" aria-hidden>
                  <Skeleton className="h-5 w-1/3" />
                  {/* A page of text being laid out. */}
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Download (primary) and open in a tab (secondary). Disabled until the PDF
// exists, so the toolbar keeps its shape while it renders.
function PdfActions({
  url,
  link,
  fileName,
}: {
  url?: string
  link?: string
  fileName: string
}) {
  const tr = useTr(AUDIT_T)
  return (
    <div className="flex items-center gap-2">
      {link ? (
        <Button asChild variant="outline" size="sm">
          <a href={link} target="_blank" rel="noreferrer">
            <ExternalLink />
            {tr("leadAuditPdfOpenTab")}
          </a>
        </Button>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled>
          <ExternalLink />
          {tr("leadAuditPdfOpenTab")}
        </Button>
      )}
      {url ? (
        <Button asChild size="sm">
          <a href={downloadUrl(url, fileName)} download={fileName}>
            <Download />
            {tr("leadAuditPdfDownload")}
          </a>
        </Button>
      ) : (
        <Button type="button" size="sm" disabled>
          <Download />
          {tr("leadAuditPdfDownload")}
        </Button>
      )}
    </div>
  )
}
